import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock, Search as SearchIcon } from 'lucide-react';
import { PageHero } from '../components/PageHero';
import { Reveal } from '../components/Reveal';
import { CTASection } from '../components/CTASection';
import { posts } from '../data/content';
import { usePageMeta } from '../components/ScrollToTop';

export default function Insights() {
  usePageMeta(
    'Insights & Blog — TheBigHub | Digital Growth Ideas',
    'Practical articles on Google Ads, SEO, social media marketing, branding, websites, AI, and business growth from TheBigHub.'
  );
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('All');

  const categories = useMemo(
    () => ['All', ...Array.from(new Set(posts.map((p) => p.category)))],
    []
  );

  const filtered = useMemo(
    () =>
      posts.filter((p) => {
        const matchesCat = category === 'All' || p.category === category;
        const q = query.trim().toLowerCase();
        const matchesQ =
          !q ||
          p.title.toLowerCase().includes(q) ||
          p.excerpt.toLowerCase().includes(q) ||
          p.category.toLowerCase().includes(q);
        return matchesCat && matchesQ;
      }),
    [query, category]
  );

  const [featured, ...rest] = filtered;

  return (
    <main data-testid="insights-page">
      <PageHero
        eyebrow="Blog & Digital Insights"
        title="Insights for Your Digital Growth."
        copy="Practical, educational reads on marketing, visibility, branding, websites, AI, and building a business that gets discovered."
      />
      <section className="bg-cream">
        <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-24">
          <div className="mb-10 flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap gap-2.5" data-testid="insights-categories">
              {categories.map((c) => (
                <button
                  key={c}
                  data-testid={`insights-filter-${c.toLowerCase().replace(/[^a-z]/g, '-')}`}
                  onClick={() => setCategory(c)}
                  className={`rounded-full px-4 py-2 text-xs font-semibold transition-all duration-300 ${
                    category === c
                      ? 'bg-forest text-white'
                      : 'border border-ink/15 bg-white text-ink hover:border-forest hover:text-forest'
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
            <label className="relative block w-full max-w-sm">
              <SearchIcon size={16} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-ink" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search insights…"
                data-testid="insights-search-input"
                aria-label="Search insights"
                className="w-full rounded-full border border-ink/15 bg-white py-3 pl-11 pr-4 text-sm text-ink outline-none transition-colors placeholder:text-ink focus:border-forest"
              />
            </label>
          </div>

          {filtered.length === 0 && (
            <p className="py-16 text-center text-ink" data-testid="insights-empty">
              No articles match your search yet — try another keyword or category.
            </p>
          )}

          {featured && (
            <Reveal>
              <Link
                to={`/insights/${featured.slug}`}
                data-testid={`insights-featured-${featured.slug}`}
                className="card-lift group mb-8 grid overflow-hidden rounded-2xl border border-ink/10 bg-white lg:grid-cols-2"
              >
                <div className="img-frame aspect-[16/10] rounded-none lg:aspect-auto lg:h-full">
                  <img src={featured.image} alt={featured.imageAlt} loading="eager" />
                </div>
                <div className="flex flex-col justify-center p-8 sm:p-12">
                  <div className="mb-4 flex items-center gap-3">
                    <span className="rounded-full bg-forest px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-white">
                      Featured
                    </span>
                    <span className="rounded-full bg-forest/10 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.16em] text-forest">
                      {featured.category}
                    </span>
                  </div>
                  <h2 className="font-display text-2xl font-bold leading-snug text-ink transition-colors group-hover:text-forest sm:text-3xl">
                    {featured.title}
                  </h2>
                  <p className="mt-4 text-sm leading-relaxed text-ink sm:text-base">{featured.excerpt}</p>
                  <p className="mt-6 flex items-center gap-3 text-xs text-ink">
                    <span>{featured.author}</span> · <span>{new Date(featured.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</span> ·
                    <span className="flex items-center gap-1"><Clock size={12} /> {featured.readingTime}</span>
                  </p>
                </div>
              </Link>
            </Reveal>
          )}

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {rest.map((p, i) => (
              <Reveal key={p.slug} delay={(i % 3) * 0.08} className="h-full">
                <Link
                  to={`/insights/${p.slug}`}
                  data-testid={`insights-card-${p.slug}`}
                  className="card-lift group flex h-full flex-col overflow-hidden rounded-2xl border border-ink/10 bg-white"
                >
                  <div className="img-frame aspect-[16/10] rounded-none">
                    <img src={p.image} alt={p.imageAlt} loading="lazy" />
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <div className="mb-3 flex items-center gap-3">
                      <span className="rounded-full bg-forest/10 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.16em] text-forest">
                        {p.category}
                      </span>
                      <span className="flex items-center gap-1 text-xs text-ink">
                        <Clock size={12} /> {p.readingTime}
                      </span>
                    </div>
                    <h2 className="font-display text-lg font-bold leading-snug text-ink transition-colors group-hover:text-forest">
                      {p.title}
                    </h2>
                    <p className="mt-2 line-clamp-2 flex-1 text-sm leading-relaxed text-ink">{p.excerpt}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <CTASection />
    </main>
  );
}
